import React, { useCallback, useRef, useState } from "react";
import Select from 'react-select/async';
import ErrorModal from '@/ErrorModal';

// Custom SingleValue for two-line display of the selected value
const CustomSingleValue = ({ label1Key, label2Key, valueKey }) => props => {
	const { data } = props;
	return (
		<div style={{ gridRowStart: 1, gridColumnStart: 1, gridRowEnd: 2, gridColumnEnd: 3, padding: '0.375rem 0.375rem' }}>
			<div style={{ fontWeight: 'bold', fontSize: '14px' }}>{data[label1Key] || data[valueKey]}</div>
			<div style={{ fontSize: '12px', color: 'gray' }}>{data[label2Key]}</div>
		</div>
	);
};

// Custom option component
const CustomOption = ({ label1Key, label2Key, valueKey }) => props => {
	const { data, innerRef, innerProps, isDisabled } = props;
	const inner_el = (
		<>
			<div style={{ fontWeight: 'bold', fontSize: '14px' }}>{data[label1Key] || data[valueKey]}</div>
			<div style={{ fontSize: '12px', color: 'gray' }}>{data[label2Key]}</div>
		</>
	);
	const el = (
		<div className="border-bottom-not-last" ref={innerRef} {...innerProps} style={{ cursor: 'default', padding: '10px' }}>
			{isDisabled ? <s>{inner_el}</s> : inner_el}
		</div>
	);
	return el;
};

const search = ({ label1Key, label2Key, options, value, valueKey }) => {
	const _value = value.toLowerCase();
	const _options = options.filter(data => {
		return (
			(data[label1Key] || data[valueKey] || '').toLowerCase().includes(_value) ||
			(data[label2Key] || '').toLowerCase().includes(_value)
		);
	});
	return _options;
};

export default ({ className = 'form-control p-0 mt-2', defaultValue, disabled, extraKeys, getData, idKey, isMulti = false, isOptionDisabled, label1Key, label2Key, onChange, options, placeholder, setOptions, valueKey = 'id' }) => {
	const defaultOptions = useRef();
	const [isLoading, setIsLoading] = useState(false);
	const inputValue = useRef('');
	const selectRef = useRef(null);
	const [error, setError] = useState();
	const [refreshCount, setRefreshCount] = useState(0);
	const incRefreshCount = () => setRefreshCount(prev => prev + 1);

	const catchError = error => {
		setError(error.toString());
	};

	const loadOptions = useCallback(_.debounce((_inputValue, cb) => {
		if (defaultOptions.current) {
			const _options = search({ label1Key, label2Key, options: defaultOptions.current, value: _inputValue, valueKey });
			cb(_options);
			return;
		}
		if (options) {
			const _options = search({ label1Key, label2Key, options, value: _inputValue, valueKey })
			cb(_options);
			return;
		}
		getData && getData({})
			.then(data => data.map(e => ({
				...(extraKeys ? extraKeys.reduce((prev, cur) => ({ ...prev, [cur]: e[cur] }), {}) : undefined),
				isDisabled: true,
				[label1Key]: e[label1Key],
				[label2Key]: e[label2Key],
				[valueKey]: e[valueKey]
			})))
			.then(options => {
				defaultOptions.current = options;
				setOptions && setOptions(defaultOptions.current);
				// Use inputValue.current during callback since user may have typed during async
				const _options = search({ label1Key, label2Key, options, value: inputValue.current, valueKey })
				cb(_options);
			})
			.catch(catchError);
	}, getData && isLoading ? 2000 : 0), [defaultOptions.current, isLoading]);

	const handleMenuOpen = useCallback(() => {
		if (options) {
			defaultOptions.current = options;
			incRefreshCount();
			return;
		}
		if (!defaultOptions.current) {
			if (!getData) {
				defaultOptions.current = options;
				incRefreshCount();
				return;
			}
			// Load the defaultOptions
			setIsLoading(true);
			getData && getData({})
				.then(data => {
					defaultOptions.current = data.map(e => ({
						...(extraKeys ? extraKeys.reduce((prev, cur) => ({ ...prev, [cur]: e[cur] }), {}) : undefined),
						[label1Key]: e[label1Key],
						[label2Key]: e[label2Key],
						[valueKey]: e[valueKey]
					}));
					setOptions && setOptions(defaultOptions.current);
					setIsLoading(false);
				});
		}
	}, [defaultOptions.current, inputValue.current, options]);

	const handleInputChange = useCallback((_inputValue, { action }) => {
		// Active typing will cancel any pending loadOptions debounce
		loadOptions.cancel();
	    // Preserve searchText on input-change, menu-close and ignore resetting it on other actions
		if (['input-change', 'menu-close'].indexOf(action) !== -1) inputValue.current = _inputValue;
		return _inputValue;
	}, [loadOptions]);

	const handleKeyDown = useCallback(e => {
		if (e.key === 'Home') {
			e.preventDefault();
			const input = selectRef.current.inputRef;
			if (e.shiftKey) {
				input.selectionStart = 0;
			} else {
				input.setSelectionRange(0, 0);
			}
		} else if (e.key === 'End') {
			e.preventDefault();
			const input = selectRef.current.inputRef;
			const len = input.value.length;
			if (e.shiftKey) {
				input.selectionEnd = len;
			} else {
				input.setSelectionRange(len, len);
			}
		}
	}, [selectRef]);

	return (
		<>
			{error && <ErrorModal message={error} onClose={() => setError()} title="Selector Error" />}
			<Select
				className={className}
				components={{
					Option: CustomOption({ label1Key, label2Key, valueKey }),
					SingleValue: CustomSingleValue({ label1Key, label2Key, valueKey })
				}}
				defaultValue={defaultValue}
				defaultOptions={defaultOptions.current}
				getOptionLabel={option => option[label1Key]}
				getOptionValue={option => option[valueKey]}
				isDisabled={disabled}
				isLoading={isLoading}
				isMulti={isMulti}
				isOptionDisabled={isOptionDisabled}
				isSearchable={true}
				loadOptions={loadOptions}
				onChange={onChange}
				onInputChange={handleInputChange}
				onKeyDown={handleKeyDown}
				onMenuOpen={handleMenuOpen}
				placeholder={placeholder}
				ref={selectRef}
				styles={{
					control: (provided, state) => {
						return {
							...provided,
							borderColor: 'var(--vs-border-color',
							...(state.isFocused ? {
								boxShadow: '0 0 0 0.25rem rgba(13, 110, 253, 0.25)',
								borderColor: '#86b7fe'
							} : undefined)
						};
					}
				}}
			/>
		</>
	);
};
